import { Button, TabsWithContent, useModal, useQueryState } from "@homebound/beam";
import { useMemo } from "react";
import { Loading } from "src/components";
import {
  BidContractPlanPricing_RevisionFragment,
  useBidContractPlanPricing_ReadyPlanOptionsQuery,
  useBidContractPlanPricing_RevisionQuery,
  useBidContractPlanPricing_ScopeLinesQuery,
} from "src/components/bid-contracts/BidContractPlanPricingPage.generated";
import { useRouteParams } from "src/hooks/useRouteParams";
import { Error } from "src/routes/Error";
import { ImportBidModal } from "src/routes/bids/components/ImportBidModal";
import { ProrateBidView } from "src/routes/bids/components/ProrateBidModal";
import { fail, isDefined, queryResult } from "src/utils";

export function BidContractPlanPricingPage() {
  const { bidContractRevisionId } = useRouteParams<{ bidContractRevisionId: string }>();
  const query = useBidContractPlanPricing_RevisionQuery({ variables: { bidContractRevisionId } });
  return queryResult(query, ({ bidContractRevision }) => (
    <BidContractPlanPricingView bidContractRevision={bidContractRevision} />
  ));
}

type BidContractPlanPricingViewProps = { bidContractRevision: BidContractPlanPricing_RevisionFragment };

function BidContractPlanPricingView(props: BidContractPlanPricingViewProps) {
  const { bidContractRevision } = props;
  const { openModal } = useModal();
  const bidPackageVersion = bidContractRevision.bidPackageVersion!;
  const sortedRpavs = useMemo(
    () => bidPackageVersion.readyPlanAggregateVersions.sortBy((rpav) => rpav.readyPlan.id),
    [bidPackageVersion.readyPlanAggregateVersions],
  );
  const [selectedTab, setSelectedTab] = useQueryState<string>("planId", sortedRpavs[0].readyPlan.id);
  const selectedReadyPlan = useMemo(() => {
    return sortedRpavs.find((rpav) => rpav.readyPlan.id === selectedTab)?.readyPlan ?? fail("ReadyPlan not found");
  }, [selectedTab, sortedRpavs]);
  const query = useBidContractPlanPricing_ScopeLinesQuery({
    variables: { bidPackageVersionId: bidPackageVersion.id, filter: { readyPlan: [selectedTab] } },
  });
  // Only the options our scope lines actually reference — see PlanBasedBidsTable.graphql
  const optionIds = useMemo(
    () =>
      (query.data?.bidPackageVersionScopeLines ?? [])
        .flatMap((sl) => sl.takeoffLineItemVersion.options.map((o) => o.id))
        .unique(),
    [query.data?.bidPackageVersionScopeLines],
  );
  const readyPlanOptionsQuery = useBidContractPlanPricing_ReadyPlanOptionsQuery({
    variables: { optionIds },
    skip: optionIds.isEmpty,
  });
  const readyPlanOptionsById = useMemo(
    () => readyPlanOptionsQuery.data?.readyPlanOptions.keyBy((option) => option.id) ?? {},
    [readyPlanOptionsQuery.data?.readyPlanOptions],
  );
  // The options query is keyed off ids read out of the scope lines, so it lands a beat later; without
  // this the view would render against an empty map and `getReadyPlanOptions` would fail.
  const optionsReady = optionIds.isEmpty || isDefined(readyPlanOptionsQuery.data);

  return (
    <TabsWithContent
      right={
        bidContractRevision.bidContract.isInternalEstimate ? undefined : (
          <Button
            label="Upload Costs"
            variant="text"
            size="sm"
            disabled={!bidContractRevision.canEdit.allowed}
            onClick={() =>
              openModal({
                content: (
                  <ImportBidModal
                    requests={[bidContractRevision.bidContract.bidRequest!]}
                    bidPackageName={bidContractRevision.bidContract.name}
                    latestBidContractRevisions={[bidContractRevision]}
                    bidPackageVersionId={bidPackageVersion.id}
                    isUnitBased={false}
                    tradePartnerId={bidContractRevision.bidContract.tradePartner?.id}
                  />
                ),
              })
            }
          />
        )
      }
      alwaysShowAllTabs
      tabs={sortedRpavs.map((rpav) => ({
        name: rpav.displayName,
        value: rpav.readyPlan.id,
        render: () => {
          // Surface option query failures directly; otherwise `readyPlanOptionsById` falls back to an
          // empty map and every option lookup blows up with a misleading "ReadyPlanOption not found".
          if (readyPlanOptionsQuery.error) return <Error error={readyPlanOptionsQuery.error} />;
          if (query.loading || readyPlanOptionsQuery.loading || !optionsReady) return <Loading />;
          return queryResult(query, ({ bidPackageVersionScopeLines }) => (
            <ProrateBidView
              bidContractRevisionId={bidContractRevision.id}
              readyPlan={selectedReadyPlan}
              readyPlanOptionsById={readyPlanOptionsById}
              scopeLineItems={bidPackageVersionScopeLines.map((sli) => sli.takeoffLineItemVersion)}
              type={bidContractRevision.bidContract.type!.code}
              existingTradeLineItems={bidContractRevision.tradeLineItems}
              modalTreatment={false}
              disabled={!bidContractRevision.canEdit.allowed}
            />
          ));
        },
      }))}
      selected={selectedTab}
      onChange={setSelectedTab}
    />
  );
}
